import React, { useEffect, useMemo, useState } from 'react';
import { HomeOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import ResponsiblePersonMultiSelect, { ResponsibleOptionUser } from '../../components/ResponsiblePersonMultiSelect';
import {
  ApprovalApproverSettings as ApprovalApproverSettingsData,
  getApprovalApproverSettings,
  updateApprovalApproverSettings
} from '../../services/api';
import '../MemberManagement.css';

type ApprovalTypeKey =
  | 'consumable'
  | 'reagent'
  | 'seal'
  | 'repair'
  | 'leave'
  | 'test'
  | 'businessTrip'
  | 'itemClaim'
  | 'financeReimbursement';

interface ApprovalTypeConfig {
  key: ApprovalTypeKey;
  title: string;
  description: string;
}

const APPROVAL_TYPES: ApprovalTypeConfig[] = [
  { key: 'consumable', title: '耗材申请', description: '配置耗材申请提交后的审批人。' },
  { key: 'reagent', title: '试剂申请', description: '配置试剂申请提交后的审批人。' },
  { key: 'seal', title: '用章申请', description: '配置用章申请提交后的审批人。' },
  { key: 'repair', title: '物品报修', description: '配置报修申请提交后的审批人。' },
  { key: 'leave', title: '请假', description: '配置请假申请提交后的审批人。' },
  { key: 'test', title: '测试申请', description: '配置测试申请提交后的审批人。' },
  { key: 'businessTrip', title: '出差申请', description: '配置出差申请提交后的审批人。' },
  { key: 'itemClaim', title: '物品申领', description: '配置物品申领提交后的审批人。' },
  { key: 'financeReimbursement', title: '财务报销', description: '配置财务报销提交后的审批人；管理员与此处成员可查看团队全部报销记录。' }
];

const buildSettings = (input?: Partial<ApprovalApproverSettingsData>): Record<ApprovalTypeKey, string[]> => {
  const defaults = APPROVAL_TYPES.reduce((acc, item) => {
    acc[item.key] = [];
    return acc;
  }, {} as Record<ApprovalTypeKey, string[]>);
  if (!input) return defaults;
  APPROVAL_TYPES.forEach((item) => {
    const list = input[item.key];
    defaults[item.key] = Array.isArray(list) ? list.filter(Boolean) : [];
  });
  return defaults;
};

const ApprovalApproverSettings: React.FC = () => {
  const [users, setUsers] = useState<ResponsibleOptionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [settings, setSettings] = useState<Record<ApprovalTypeKey, string[]>>(() => buildSettings());
  const [reagentRequireSupervisorApproval, setReagentRequireSupervisorApproval] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const [usersResponse, settingsResponse] = await Promise.all([
          fetch('/api/auth/users', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }),
          getApprovalApproverSettings()
        ]);
        const userData = await usersResponse.json();
        if (Array.isArray(userData)) {
          setUsers(userData.filter((u: ResponsibleOptionUser) => u.role !== '超级管理员'));
        } else {
          setErrorMsg('获取成员列表失败，请稍后重试');
        }
        if (settingsResponse.success && settingsResponse.data) {
          setSettings(buildSettings(settingsResponse.data));
          setReagentRequireSupervisorApproval(Boolean(settingsResponse.data.reagentRequireSupervisorApproval));
        } else if (settingsResponse.error) {
          setErrorMsg(settingsResponse.error);
        }
      } catch (error) {
        setErrorMsg('获取成员列表失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const configuredCount = useMemo(
    () => APPROVAL_TYPES.filter((item) => settings[item.key]?.length > 0).length,
    [settings]
  );

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const response = await updateApprovalApproverSettings({
        consumable: settings.consumable || [],
        reagent: settings.reagent || [],
        reagentRequireSupervisorApproval,
        seal: settings.seal || [],
        repair: settings.repair || [],
        leave: settings.leave || [],
        test: settings.test || [],
        businessTrip: settings.businessTrip || [],
        itemClaim: settings.itemClaim || [],
        financeReimbursement: settings.financeReimbursement || []
      });
      if (!response.success || !response.data) {
        setErrorMsg(response.error || '保存失败，请稍后重试');
        return;
      }
      setSettings(buildSettings(response.data));
      setReagentRequireSupervisorApproval(Boolean(response.data.reagentRequireSupervisorApproval));
      setSuccessMsg('审批人设置已保存');
    } catch (error) {
      setErrorMsg('保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>审批中心</span>
        <span className="mm-crumb-sep">/</span>
        <span>审批人设置</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">审批人设置</h1>
          <p className="mm-subtitle mb-0">为不同类型审批流程设置审批人，支持多选。</p>
        </div>
        <button type="button" className="mm-btn-primary" onClick={handleSave} disabled={saving || loading}>
          <LimsNavIcon name="save" sizePx={18} />
          {saving ? '保存中...' : '保存设置'}
        </button>
      </header>

      {errorMsg && (
        <Alert variant="danger" className="mb-3" onClose={() => setErrorMsg('')} dismissible>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert variant="success" className="mb-3" onClose={() => setSuccessMsg('')} dismissible>
          {successMsg}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-start gap-2">
            <LimsNavIcon name="info" sizePx={20} />
            <div>
              <div style={{ fontWeight: 600 }}>仪器预约审批说明</div>
              <div className="text-muted" style={{ fontSize: 14 }}>
                仪器预约审批固定由设备负责人处理，此处无需额外配置。设备负责人可在仪器库存管理中进行多选维护。
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div className="text-muted">已配置 {configuredCount} / {APPROVAL_TYPES.length} 种审批类型</div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              setSettings(
                APPROVAL_TYPES.reduce((acc, item) => {
                  acc[item.key] = [];
                  return acc;
                }, {} as Record<ApprovalTypeKey, string[]>)
              )
            }
          >
            清空当前选择
          </Button>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-2 mb-0">正在加载成员列表...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {APPROVAL_TYPES.map((item) => (
            <Card key={item.key}>
              <Card.Body>
                <Form.Group className="mb-0">
                  <Form.Label style={{ fontWeight: 600 }}>{item.title}</Form.Label>
                  <div style={{ marginBottom: 10, color: '#6c757d', fontSize: 13 }}>{item.description}</div>
                  <ResponsiblePersonMultiSelect
                    users={users}
                    selectedNames={settings[item.key] || []}
                    onChange={(nextSelectedNames) =>
                      setSettings((prev) => ({
                        ...prev,
                        [item.key]: nextSelectedNames
                      }))
                    }
                    helperText="可多选。任一审批人均可处理该类型待审批单。"
                  />
                  {item.key === 'reagent' && (
                    <Form.Check
                      type="switch"
                      id="reagent-supervisor-switch"
                      className="mt-3"
                      label="开启导师一级审批（开启后：导师先审，再流转给试剂审批人）"
                      checked={reagentRequireSupervisorApproval}
                      onChange={(e) => setReagentRequireSupervisorApproval(e.target.checked)}
                    />
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalApproverSettings;
